import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  SendHorizonal,
  Sparkles,
  Layers,
  Paperclip,
  X,
  Brain,
  Wand2,
  Edit3,
  Plus,
  Trash2,
  Palette,
  Image as ImageIcon,
  HelpCircle,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Copy,
  ArrowLeftRight,
  Link2,
  SlidersHorizontal,
  Table2,
  Type,
  Ban,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { usePresentationChat } from '@/api/usePresentationChat';
import type { AgentStep, AgentToolName, ChatMessage } from '@/api/usePresentationChat';
import type { DeckV2, SlideV2 } from '@/types/slide';
import styles from './PresentationChat.module.css';

interface PresentationChatProps {
  presentationId: string;
  currentSlideIndex: number;
  currentSlide: SlideV2 | null;
  onDeckUpdate?: (deck: DeckV2, affectedSlides?: number[]) => void;
}

interface QuickAction {
  label: string;
  msg: string;
}

interface UploadedImage {
  url: string;
  name: string;
}

// ── Transcript helpers ──────────────────────────────────────────────────

const TOOL_META: Record<
  AgentToolName,
  { label: string; Icon: React.ComponentType<{ size?: number }> }
> = {
  thinking: { label: 'Thinking', Icon: Brain },
  edit_slide: { label: 'Editing slide', Icon: Edit3 },
  replace_slide: { label: 'Redesigning slide', Icon: Wand2 },
  add_slide: { label: 'Adding slide', Icon: Plus },
  delete_slide: { label: 'Deleting slide', Icon: Trash2 },
  duplicate_slide: { label: 'Duplicating slide', Icon: Copy },
  move_slide: { label: 'Moving slide', Icon: ArrowLeftRight },
  change_theme: { label: 'Updating theme', Icon: Palette },
  regenerate_image: { label: 'Generating image', Icon: ImageIcon },
  set_image: { label: 'Placing image', Icon: Link2 },
  apply_image_effect: { label: 'Editing image', Icon: SlidersHorizontal },
  edit_cost_table: { label: 'Editing cost table', Icon: Table2 },
  edit_block: { label: 'Editing block', Icon: Type },
  ask_user: { label: 'Question', Icon: HelpCircle },
  finish: { label: 'Done', Icon: CheckCircle2 },
  cannot_do: { label: 'Not supported', Icon: Ban },
  system: { label: 'System', Icon: AlertTriangle },
};

const describeToolInput = (step: AgentStep): string => {
  const input = step.input ?? {};
  const get = (key: string): string | number | undefined => {
    const v = (input as Record<string, unknown>)[key];
    return typeof v === 'string' || typeof v === 'number' ? v : undefined;
  };

  switch (step.tool) {
    case 'edit_slide': {
      const idx = get('slide_index');
      const patches = (input as { patches?: Record<string, unknown> }).patches ?? {};
      const fields = Object.keys(patches).join(', ') || '—';
      return typeof idx === 'number' ? `slide ${idx + 1} · ${fields}` : fields;
    }
    case 'replace_slide': {
      const idx = get('slide_index');
      return typeof idx === 'number' ? `slide ${idx + 1}` : '';
    }
    case 'add_slide': {
      const after = get('after_slide_index');
      return typeof after === 'number' ? `after slide ${after + 1}` : '';
    }
    case 'delete_slide': {
      const idx = get('slide_index');
      return typeof idx === 'number' ? `slide ${idx + 1}` : '';
    }
    case 'duplicate_slide': {
      const idx = get('slide_index');
      return typeof idx === 'number' ? `slide ${idx + 1}` : '';
    }
    case 'move_slide': {
      const from = get('from_index');
      const to = get('to_index');
      if (typeof from === 'number' && typeof to === 'number') {
        return `slide ${from + 1} → ${to + 1}`;
      }
      return '';
    }
    case 'change_theme': {
      const keys = Object.keys(input).filter((k) => (input as Record<string, unknown>)[k]);
      return keys.join(', ') || 'theme';
    }
    case 'regenerate_image': {
      const idx = get('slide_index');
      const target = String(get('target') ?? '');
      const parts: string[] = [];
      if (typeof idx === 'number') parts.push(`slide ${idx + 1}`);
      if (target) parts.push(target);
      return parts.join(' · ');
    }
    case 'set_image': {
      const idx = get('slide_index');
      const target = String(get('target') ?? '');
      const parts: string[] = [];
      if (typeof idx === 'number') parts.push(`slide ${idx + 1}`);
      if (target) parts.push(target);
      return parts.join(' · ');
    }
    case 'apply_image_effect': {
      const idx = get('slide_index');
      const effects = (input as { effects?: Array<{ type?: string }> }).effects ?? [];
      const types = effects.map((e) => e.type).filter(Boolean).join(', ');
      const slidePart = typeof idx === 'number' ? `slide ${idx + 1}` : '';
      return [slidePart, types].filter(Boolean).join(' · ');
    }
    case 'edit_cost_table': {
      const idx = get('slide_index');
      const action = String(get('action') ?? '');
      const slidePart = typeof idx === 'number' ? `slide ${idx + 1}` : '';
      return [slidePart, action].filter(Boolean).join(' · ');
    }
    case 'edit_block': {
      const idx = get('slide_index');
      const blockType = String(get('block_type') ?? '');
      const blockIndex = get('block_index');
      const slidePart = typeof idx === 'number' ? `slide ${idx + 1}` : '';
      const blockPart =
        blockType + (typeof blockIndex === 'number' ? ` #${blockIndex + 1}` : '');
      return [slidePart, blockPart].filter(Boolean).join(' · ');
    }
    case 'ask_user':
      return String(get('question') ?? '').slice(0, 120);
    case 'finish':
      return String(get('summary') ?? '').slice(0, 120);
    case 'cannot_do':
      return String(get('reason') ?? '').slice(0, 120);
    default:
      return '';
  }
};

// ── Pending-state heuristic ─────────────────────────────────────────────
// The agent runs server-side and only returns when the whole turn is done,
// so the user otherwise sees a static "Thinking" card.  We infer what's
// likely happening from the user's last message and cycle through realistic
// stage labels so the wait feels purposeful.

interface PendingStage {
  label: string;
  Icon: React.ComponentType<{ size?: number }>;
}

const inferPendingStages = (message: string, hasAttachment: boolean): PendingStage[] => {
  const lower = (message || '').toLowerCase();

  // Very specific image effect intents come first.
  const isCorners = /round\s*corner|rounded|esquinas/.test(lower);
  const isCrop = /\bcrop|recor[ti]ar|trim/.test(lower);
  const isDark = /darker|brighter|brightness|brillo|oscur|contrast/.test(lower);
  const isBW = /grayscale|gray\s*scale|black\s*and\s*white|blanco y negro|sepia/.test(lower);
  const isBlur = /\bblur|desenfoc/.test(lower);
  const isFlip = /\bflip|mirror|voltear|espejo/.test(lower);
  const imageEffect = isCorners || isCrop || isDark || isBW || isBlur || isFlip;

  if (imageEffect) {
    return [
      { label: 'Reading the slide', Icon: Brain },
      { label: 'Downloading image', Icon: ImageIcon },
      { label: 'Applying effect', Icon: SlidersHorizontal },
      { label: 'Uploading new image', Icon: ImageIcon },
      { label: 'Rebuilding PPTX', Icon: Layers },
    ];
  }

  if (hasAttachment) {
    return [
      { label: 'Reading your image', Icon: ImageIcon },
      { label: 'Placing it on the slide', Icon: Link2 },
      { label: 'Rebuilding PPTX', Icon: Layers },
    ];
  }

  const isImage =
    /\bimage|imagen|photo|foto|picture|background|fondo|logo|render|illustration/.test(lower) ||
    /generate|regenerate|nueva\s+imagen|cambia(?:r)?\s+(?:la\s+)?imagen/.test(lower);
  if (isImage) {
    return [
      { label: 'Planning the new image', Icon: Brain },
      { label: 'Generating image with Gemini', Icon: ImageIcon },
      { label: 'Uploading to storage', Icon: Wand2 },
      { label: 'Rebuilding PPTX', Icon: Layers },
    ];
  }

  const isTheme =
    /\bcolor|palette|paleta|theme|tema|font|tipograf|dark|light|brand/.test(lower);
  if (isTheme) {
    return [
      { label: 'Picking colors and fonts', Icon: Palette },
      { label: 'Updating the theme', Icon: Wand2 },
      { label: 'Rebuilding PPTX', Icon: Layers },
    ];
  }

  const isCost = /cost|price|precio|presupuesto|table|tabla|fila|row/.test(lower);
  if (isCost) {
    return [
      { label: 'Reading the cost table', Icon: Table2 },
      { label: 'Updating cells', Icon: Edit3 },
      { label: 'Rebuilding PPTX', Icon: Layers },
    ];
  }

  const isStructure =
    /\badd\s+slide|new\s+slide|nueva\s+diapositiva|delete\s+slide|borrar|remove\s+slide|duplicate|move\s+slide|reorder|swap/.test(
      lower,
    );
  if (isStructure) {
    return [
      { label: 'Reorganizing slides', Icon: ArrowLeftRight },
      { label: 'Applying changes', Icon: Edit3 },
      { label: 'Rebuilding PPTX', Icon: Layers },
    ];
  }

  const isRedesign = /\bredesign|rediseñ|make\s+it\s+(?:better|nicer|more)|improve|mejor|fresh/.test(
    lower,
  );
  if (isRedesign) {
    return [
      { label: 'Analyzing the current slide', Icon: Brain },
      { label: 'Designing a new layout', Icon: Wand2 },
      { label: 'Generating images if needed', Icon: ImageIcon },
      { label: 'Rebuilding PPTX', Icon: Layers },
    ];
  }

  const isText =
    /\btitle|subtitle|copy|texto|text|bullet|short|larger|wording|rewrite|reescrib|traduc|translate|typo|fix\s+the\s+wording/.test(
      lower,
    );
  if (isText) {
    return [
      { label: 'Reading the slide', Icon: Brain },
      { label: 'Rewriting the copy', Icon: Edit3 },
      { label: 'Rebuilding PPTX', Icon: Layers },
    ];
  }

  // Default — a generic 3-stage cycle.
  return [
    { label: 'Thinking', Icon: Brain },
    { label: 'Calling tools', Icon: Wand2 },
    { label: 'Rebuilding PPTX', Icon: Layers },
  ];
};

const usePendingStage = (
  isPending: boolean,
  message: string,
  hasAttachment: boolean,
): PendingStage => {
  const stages = useMemo(() => inferPendingStages(message, hasAttachment), [message, hasAttachment]);
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    if (!isPending) {
      setStageIndex(0);
      return undefined;
    }
    setStageIndex(0);
    const id = window.setInterval(() => {
      setStageIndex((prev) => Math.min(prev + 1, stages.length - 1));
    }, 2400);
    return () => window.clearInterval(id);
  }, [isPending, stages.length]);

  return stages[Math.min(stageIndex, stages.length - 1)] ?? { label: 'Working', Icon: Brain };
};

const TranscriptStep = ({ step }: { step: AgentStep }) => {
  const meta = TOOL_META[step.tool] ?? TOOL_META.system;
  const { Icon } = meta;

  if (step.tool === 'thinking') {
    return (
      <div className={styles.stepThinking}>
        <Brain size={12} className={styles.stepThinkingIcon} />
        <span className={styles.stepThinkingText}>{step.result}</span>
      </div>
    );
  }

  const subtitle = describeToolInput(step);
  const isQuestionOrFinish = step.tool === 'ask_user' || step.tool === 'finish';

  return (
    <div
      className={`${styles.step} ${step.ok ? styles.stepOk : styles.stepError} ${
        isQuestionOrFinish ? styles.stepTerminal : ''
      }`}
    >
      <div className={styles.stepIcon}>
        <Icon size={13} />
      </div>
      <div className={styles.stepBody}>
        <div className={styles.stepHeader}>
          <span className={styles.stepLabel}>{meta.label}</span>
          {subtitle && <span className={styles.stepSubtitle}>{subtitle}</span>}
        </div>
        {step.result && !isQuestionOrFinish && (
          <p className={styles.stepResult}>{step.result}</p>
        )}
      </div>
      {!step.ok && <XCircle size={13} className={styles.stepStatusIcon} />}
    </div>
  );
};

const getQuickActions = (slide: SlideV2 | null, hasImages: boolean): QuickAction[] => {
  const base: QuickAction[] = [
    { label: 'Redesign', msg: 'Redesign this slide — new layout, fresh visuals, same message' },
    { label: 'Simplify', msg: 'Simplify this slide — less text, more impact' },
    { label: 'More visual', msg: 'Make this slide more visual and eye-catching' },
    { label: 'Shorten text', msg: 'Shorten the title and bullets so nothing feels crowded' },
    { label: 'Change layout', msg: 'Try a different component layout for this slide' },
    { label: 'New image', msg: 'Generate a new image for this slide with a different style' },
    { label: 'Change colors', msg: 'Change the deck color palette to something more modern' },
    { label: 'Change fonts', msg: 'Try different title and body fonts across the deck' },
    { label: 'Add slide', msg: 'Add a new slide after this one with related content' },
    { label: 'Delete slide', msg: 'Delete this slide' },
  ];

  if (!slide) return base;

  if (slide.component.startsWith('content_')) {
    base.push({
      label: 'Showcase',
      msg: 'Convert this slide to a showcase layout with a large image',
    });
  }
  if (slide.component.startsWith('data_')) {
    base.push({ label: 'Change style', msg: 'Try a different visualization style' });
  }

  if (hasImages) {
    base.unshift({
      label: 'Use attached image',
      msg: 'Add the attached image to this slide as the main visual',
    });
  }

  return base;
};

export const PresentationChat = ({
  presentationId,
  currentSlideIndex,
  currentSlide,
  onDeckUpdate,
}: PresentationChatProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [attachedImages, setAttachedImages] = useState<UploadedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingContext, setPendingContext] = useState<{ message: string; hasAttachment: boolean }>({
    message: '',
    hasAttachment: false,
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatMutation = usePresentationChat(presentationId);
  const pendingStage = usePendingStage(
    chatMutation.isPending,
    pendingContext.message,
    pendingContext.hasAttachment,
  );

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, chatMutation.isPending, scrollToBottom]);

  const autoGrowTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = 22;
    const maxHeight = lineHeight * 3 + 24;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    autoGrowTextarea();
  };

  // Upload images to GCS
  const uploadImages = useCallback(async (files: File[]): Promise<UploadedImage[]> => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));

    const res = await api.post<{ uploaded: Array<{ url: string; name: string }> }>(
      API_ROUTES.uploadPresentationImage,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data.uploaded;
  }, []);

  // Handle file input change
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length === 0) return;

      setIsUploading(true);
      try {
        const uploaded = await uploadImages(files);
        setAttachedImages((prev) => [...prev, ...uploaded]);
        toast.success(`${uploaded.length} image${uploaded.length > 1 ? 's' : ''} uploaded`);
      } catch {
        toast.error('Failed to upload image');
      } finally {
        setIsUploading(false);
        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [uploadImages],
  );

  // Handle paste from clipboard
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length === 0) return;

      e.preventDefault();
      setIsUploading(true);
      try {
        const uploaded = await uploadImages(imageFiles);
        setAttachedImages((prev) => [...prev, ...uploaded]);
        toast.success('Image pasted and uploaded');
      } catch {
        toast.error('Failed to upload pasted image');
      } finally {
        setIsUploading(false);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [uploadImages]);

  const removeAttachedImage = useCallback((index: number) => {
    setAttachedImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSend = useCallback(
    (text?: string) => {
      const msg = (text ?? input).trim();
      if (!msg || chatMutation.isPending) return;

      const imageUrls = attachedImages.map((img) => img.url);

      // Build conversation history from prior turns BEFORE appending the
      // new user message.  Each assistant turn is flattened to a plain
      // string that captures the question (if any) + its option chips,
      // so the agent can see what it already asked and what the user
      // previously replied.
      const history = messages.map((m) => {
        if (m.role === 'user') {
          return { role: 'user' as const, content: m.content };
        }
        const parts: string[] = [];
        if (m.pendingQuestion) {
          parts.push(m.pendingQuestion);
          if (m.pendingQuestionOptions && m.pendingQuestionOptions.length > 0) {
            parts.push(`Options: ${m.pendingQuestionOptions.join(' | ')}`);
          }
        } else if (m.content) {
          parts.push(m.content);
        }
        if (m.changedSlides && m.changedSlides.length > 0) {
          parts.push(
            `(applied: slide${m.changedSlides.length > 1 ? 's' : ''} ${m.changedSlides
              .map((n) => n + 1)
              .join(', ')})`,
          );
        }
        return { role: 'assistant' as const, content: parts.join('\n') || '…' };
      });

      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: msg,
          timestamp: Date.now(),
          attachedImages: attachedImages.length > 0 ? [...attachedImages] : undefined,
        },
      ]);
      setInput('');
      setPendingContext({ message: msg, hasAttachment: attachedImages.length > 0 });
      setAttachedImages([]);

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      chatMutation.mutate(
        {
          message: msg,
          current_slide_index: currentSlideIndex,
          image_urls: imageUrls.length > 0 ? imageUrls : undefined,
          history: history.length > 0 ? history : undefined,
        },
        {
          onSuccess: (data) => {
            const changedSlides = data.affected_slides ?? [];
            const isThemeChange = data.transcript.some(
              (s) => s.tool === 'change_theme' && s.ok,
            );

            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: data.pending_question || data.summary || '',
                timestamp: Date.now(),
                changedSlides: isThemeChange ? [] : changedSlides,
                isThemeChange,
                transcript: data.transcript,
                pendingQuestion: data.pending_question,
                pendingQuestionOptions: data.pending_question_options ?? [],
              },
            ]);
            if (changedSlides.length > 0) {
              onDeckUpdate?.(data.deck_json, changedSlides);
            }
          },
          onError: (error) => {
            const apiError = error as {
              response?: { status?: number; data?: { detail?: string } };
              code?: string;
            };
            const status = apiError.response?.status;
            const isTimeout = apiError.code === 'ECONNABORTED' || status === 504 || status === 502;

            const userMessage = isTimeout
              ? 'The request took too long. Try a simpler change or fewer slides.'
              : 'Sorry, there was an error. Please try again.';

            toast.error(
              isTimeout ? 'Timeout — try a simpler change' : 'Error processing your request',
            );
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content: userMessage,
                timestamp: Date.now(),
              },
            ]);
          },
        },
      );
    },
    [input, chatMutation, currentSlideIndex, onDeckUpdate, attachedImages, messages],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickActions = getQuickActions(currentSlide, attachedImages.length > 0);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Sparkles size={15} className={styles.headerIcon} />
          <span>AI Editor</span>
        </div>
        {currentSlide && (
          <div className={styles.slideIndicator}>
            <Layers size={13} />
            <span>Slide {currentSlideIndex + 1}</span>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className={styles.messagesArea}>
        {messages.length === 0 && (
          <div className={styles.emptyState}>
            <Sparkles size={28} className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>Edit with AI</p>
            <p className={styles.emptySubtitle}>
              Ask for a redesign, new text, a different layout, fresh images, a new
              color palette, or add/delete slides. Paste or attach images to drop
              them into a slide. Changes apply to the PPTX automatically.
            </p>
          </div>
        )}

        {messages.map((msg, i) => {
          if (msg.role === 'user') {
            return (
              <div key={i} className={`${styles.messageRow} ${styles.messageRowUser}`}>
                <div className={`${styles.bubble} ${styles.bubbleUser}`}>
                  {msg.attachedImages && msg.attachedImages.length > 0 && (
                    <div className={styles.messageImages}>
                      {msg.attachedImages.map((img, imgIdx) => (
                        <img
                          key={imgIdx}
                          src={img.url}
                          alt={img.name}
                          className={styles.messageImageThumb}
                        />
                      ))}
                    </div>
                  )}
                  <p className={styles.bubbleText}>{msg.content}</p>
                </div>
              </div>
            );
          }

          // Assistant message — render transcript + summary/question.
          const transcript = msg.transcript ?? [];
          const workingSteps = transcript.filter(
            (s) => s.tool !== 'finish' && s.tool !== 'ask_user',
          );
          return (
            <div key={i} className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
              <div className={styles.assistantAvatar}>
                <Sparkles size={12} />
              </div>
              <div className={styles.assistantColumn}>
                {workingSteps.length > 0 && (
                  <div className={styles.transcript}>
                    {workingSteps.map((step, stepIdx) => (
                      <TranscriptStep key={stepIdx} step={step} />
                    ))}
                  </div>
                )}

                {msg.pendingQuestion ? (
                  <div className={styles.pendingQuestion}>
                    <div className={styles.pendingQuestionHeader}>
                      <HelpCircle size={14} />
                      <span>Quick question</span>
                    </div>
                    <p className={styles.pendingQuestionText}>{msg.pendingQuestion}</p>
                    {msg.pendingQuestionOptions && msg.pendingQuestionOptions.length > 0 && (
                      <div className={styles.pendingOptions}>
                        {msg.pendingQuestionOptions.map((opt, optIdx) => (
                          <button
                            key={optIdx}
                            type="button"
                            className={styles.pendingOption}
                            onClick={() => handleSend(opt)}
                            disabled={chatMutation.isPending}
                          >
                            <span className={styles.pendingOptionNum}>{optIdx + 1}</span>
                            <span className={styles.pendingOptionLabel}>{opt}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  msg.content && (
                    <div className={`${styles.bubble} ${styles.bubbleAssistant}`}>
                      <div className={styles.bubbleMarkdown}>
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                      {msg.isThemeChange && (
                        <div className={styles.changedSlidesTag}>
                          <Sparkles size={11} />
                          <span>Theme updated</span>
                        </div>
                      )}
                      {!msg.isThemeChange &&
                        msg.changedSlides &&
                        msg.changedSlides.length > 0 && (
                          <div className={styles.changedSlidesTag}>
                            <Layers size={11} />
                            <span>
                              Slide{msg.changedSlides.length > 1 ? 's' : ''}{' '}
                              {msg.changedSlides.map((n) => n + 1).join(', ')} updated
                            </span>
                          </div>
                        )}
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })}

        {/* Pending indicator — cycles a heuristic-based stage label so the
            user sees what the agent is likely doing instead of a static
            "Thinking" placeholder. */}
        {chatMutation.isPending && (() => {
          const PendingIcon = pendingStage.Icon;
          return (
            <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
              <div className={styles.assistantAvatar}>
                <Sparkles size={12} />
              </div>
              <div className={styles.pendingCard}>
                <div className={styles.pendingRow}>
                  <PendingIcon size={13} />
                  <span className={styles.pendingLabel}>{pendingStage.label}</span>
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                </div>
                <div className={styles.pendingShimmer} />
              </div>
            </div>
          );
        })()}

        <div ref={messagesEndRef} />
      </div>

      {/* Attached images preview */}
      {attachedImages.length > 0 && (
        <div className={styles.attachedPreview}>
          {attachedImages.map((img, i) => (
            <div key={i} className={styles.attachedThumb}>
              <img src={img.url} alt={img.name} className={styles.attachedImg} />
              <button
                type="button"
                className={styles.removeAttached}
                onClick={() => removeAttachedImage(i)}
                aria-label="Remove image"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div className={styles.quickActions}>
        {quickActions.map((action) => (
          <button
            key={action.label}
            className={styles.chip}
            onClick={() => handleSend(action.msg)}
            disabled={chatMutation.isPending}
            type="button"
          >
            {action.label}
          </button>
        ))}
      </div>

      {/* Input area */}
      <div className={styles.inputArea}>
        <button
          type="button"
          className={styles.attachButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || chatMutation.isPending}
          aria-label="Attach image"
        >
          {isUploading ? (
            <div className={styles.uploadSpinner} />
          ) : (
            <Paperclip size={16} />
          )}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelect}
          className={styles.hiddenFileInput}
        />

        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={
            attachedImages.length > 0
              ? 'Describe where to place the image…'
              : 'Redesign slide 2, change colors, shorten bullets…'
          }
          rows={1}
          disabled={chatMutation.isPending}
        />
        <button
          className={styles.sendButton}
          onClick={() => handleSend()}
          disabled={(!input.trim() && attachedImages.length === 0) || chatMutation.isPending}
          type="button"
          aria-label="Send message"
        >
          <SendHorizonal size={16} />
        </button>
      </div>
    </div>
  );
};
